import * as React from "react"
import {
  Flex,
  HStack,
  Heading,
  Tooltip,
  Button,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Wrap,
  PopoverFooter,
  ButtonProps,
  Text,
  Center,
  Circle,
} from "@chakra-ui/react"
import { useQuery } from "react-query"
import { BsListUl } from "react-icons/bs"
import { RiMap2Line } from "react-icons/ri"
import { TOPBAR_HEIGHT } from "../lib/config"
import { BikeState } from "../lib/types/bike"
import { BikeModelEntity } from "../lib/types/bikeModel"
import { SearchBar } from "./SearchBar"
import { useOrgs } from "./providers/OrgProvider"
import { getBikeStates } from "../lib/static/bike"
import { useGetCustomTagGroupsQuery } from "../lib/hooks/queries/useGetCustomTags"
import { NoData } from "./NoData"
import { BikeCreate } from "./BikeCreate"
import { useFeatFlags } from "./providers/FeatFlagProvider"
import { isSupervisor } from "../lib/helpers/role"
import { useMe } from "./providers/MeProvider"

interface BikeModelsResponse {
  count: number
  results: BikeModelEntity[]
}

interface Props {
  bikeStates: BikeState[]
  bikeModels: string[]
  bikeTags: string[]
  search: string
  setSearch: (search: string) => void
  mapView: boolean
  setMapView: (mapView: boolean) => void
  setBikeStates: (value: BikeState[]) => void
  setBikeModels: (value: string[]) => void
  setBikeTags: (value: string[]) => void
  forceRefresh: () => void
}

export const BikesTopbar = (props: Props) => {
  const {
    setBikeStates,
    setBikeModels,
    setBikeTags,
    bikeModels,
    bikeStates,
    bikeTags,
    search,
    setSearch,
    mapView,
    setMapView,
  } = props
  const { currentOrgs } = useOrgs()
  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)
  const { tagGroups } = useGetCustomTagGroupsQuery()
  const tags = tagGroups.map(({ declarations }) => declarations).flat()
  const { bike_admin_features, asset_deployed_state } = useFeatFlags()
  const me = useMe()

  const { data } = useQuery<BikeModelsResponse, string>(
    `bicycle_models/?organizations=${currentOrgsUuids.join()}`,
    {
      staleTime: Infinity,
    },
  )
  const models = data?.results || []

  const toggleState = (state: BikeState) => {
    if (bikeStates.includes(state)) {
      setBikeStates(bikeStates.filter((s) => s !== state))
    } else {
      setBikeStates([...bikeStates, state])
    }
  }
  const toggleModel = (model: string) => {
    if (bikeModels.includes(model)) {
      setBikeModels(bikeModels.filter((s) => s !== model))
    } else {
      setBikeModels([...bikeModels, model])
    }
  }
  const toggleTag = (id: string) => {
    if (bikeTags.includes(id)) {
      setBikeTags(bikeTags.filter((t) => t !== id))
    } else {
      setBikeTags([...bikeTags, id])
    }
  }

  const availableBikeStates = React.useMemo(
    () => getBikeStates(asset_deployed_state?.visible),
    [asset_deployed_state],
  )

  return (
    <Flex w="100%" align="center" justify="space-between" p={6} h={TOPBAR_HEIGHT}>
      <HStack spacing={6} align="center" flex={1}>
        <Heading as="h3" color="gray.700">
          Assets
        </Heading>
        <Flex>
          <Tooltip label="Map view" hasArrow openDelay={500} zIndex={10}>
            <Button
              borderTopRightRadius={0}
              borderBottomRightRadius={0}
              size="sm"
              colorScheme={mapView ? "blue" : "gray"}
              variant={mapView ? "solid" : "outline"}
              onClick={() => setMapView(true)}
            >
              <Box as={RiMap2Line} boxSize="16px" color={mapView ? "white" : "black"} />
            </Button>
          </Tooltip>
          <Tooltip label="Table view" hasArrow openDelay={500} zIndex={10}>
            <Button
              borderTopLeftRadius={0}
              borderBottomLeftRadius={0}
              colorScheme={!mapView ? "blue" : "gray"}
              size="sm"
              variant={!mapView ? "solid" : "outline"}
              onClick={() => setMapView(false)}
            >
              <Box as={BsListUl} boxSize="16px" color={!mapView ? "white" : "black"} />
            </Button>
          </Tooltip>
        </Flex>
      </HStack>
      <HStack justify="flex-end" spacing={2} align="center" zIndex={11}>
        {bike_admin_features?.visible && isSupervisor(me.roles) && (
          <BikeCreate forceRefresh={props.forceRefresh} />
        )}
        <Popover>
          <PopoverTrigger>
            <Button variant="outline">
              {tags.length === 0 ? 0 : bikeTags.length === tags.length ? "All" : bikeTags.length} • Asset tags
            </Button>
          </PopoverTrigger>
          <PopoverContent zIndex={6}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>Filter by tag</PopoverHeader>
            <PopoverBody pt={4}>
              <Wrap spacing={2} pb={2}>
                {tags.length > 0 ? (
                  tagGroups.map(({ name, declarations }) => {
                    return (
                      <Box key={name}>
                        <Text fontSize="0.8rem" fontWeight={550} mb={1}>
                          {name}
                        </Text>
                        {declarations.map((tag) => {
                          return (
                            <TagFilterItem
                              key={tag.uuid}
                              colorScheme="gray"
                              onClick={() => toggleTag(tag.uuid)}
                              active={bikeTags.includes(tag.uuid)}
                            >
                              <Circle bg={tag.color} size="15px" mr={2} />
                              {tag.name}
                            </TagFilterItem>
                          )
                        })}
                      </Box>
                    )
                  })
                ) : (
                  <Center w="100%" py={6}>
                    <NoData>No Tags</NoData>
                  </Center>
                )}
              </Wrap>
            </PopoverBody>
            {tags.length > 0 && (
              <PopoverFooter mt={2} minH="50px">
                <Flex justify="flex-end" align="center">
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() =>
                      setBikeTags(bikeTags.length === tags.length ? [] : tags.map((s) => s.uuid))
                    }
                  >
                    {bikeTags.length === tags.length ? "Unselect all" : "Select all"}
                  </Button>
                </Flex>
              </PopoverFooter>
            )}
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger>
            <Button variant="outline">
              {bikeStates.length === availableBikeStates.length ? "All" : bikeStates.length} • Asset states
            </Button>
          </PopoverTrigger>
          <PopoverContent zIndex={6}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>Toggle asset states</PopoverHeader>
            <PopoverBody pt={4}>
              <Wrap spacing={2} pb={2}>
                {availableBikeStates.map((state) => (
                  <FilterItem
                    key={state.value}
                    colorScheme={state.color}
                    onClick={() => toggleState(state.value)}
                    active={bikeStates.includes(state.value)}
                    label={state.label}
                  />
                ))}
              </Wrap>
            </PopoverBody>
            <PopoverFooter mt={2} minH="50px">
              <Flex justify="flex-end" align="center">
                {bikeStates.length !== availableBikeStates.length && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() => setBikeStates(availableBikeStates.map((s) => s.value))}
                  >
                    Show all
                  </Button>
                )}
              </Flex>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger>
            <Button variant="outline">
              {bikeModels.length === models.length || bikeModels.length === 0 ? "All" : bikeModels.length} •
              Asset models
            </Button>
          </PopoverTrigger>
          <PopoverContent zIndex={6}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>Toggle asset models</PopoverHeader>
            <PopoverBody pt={4}>
              <Wrap spacing={1}>
                {models.map((model) => (
                  <FilterItem
                    key={model.uuid}
                    colorScheme="gray"
                    label={model.name}
                    onClick={() => toggleModel(model.uuid)}
                    active={bikeModels.includes(model.uuid)}
                  />
                ))}
              </Wrap>
              <PopoverFooter pb={0} mt={2}>
                <Flex justify="flex-end">
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() =>
                      setBikeModels(bikeModels.length === models.length ? [] : models.map((s) => s.uuid))
                    }
                  >
                    {bikeModels.length === models.length ? "Unselect all" : "Select all"}
                  </Button>
                </Flex>
              </PopoverFooter>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <SearchBar onSearchSubmit={setSearch} search={search} />
      </HStack>
    </Flex>
  )
}
interface FilterItemProps {
  active: boolean
  label: string
}
function FilterItem({ active, label, ...props }: FilterItemProps & ButtonProps) {
  return (
    <Button variant={active ? "solid" : "outline"} size="sm" borderWidth="1px" {...props} mr={1} mb={1}>
      {label}
    </Button>
  )
}
interface TagFilterItemProps {
  active: boolean
}
function TagFilterItem({ active, ...props }: TagFilterItemProps & ButtonProps) {
  return (
    <Button variant={active ? "solid" : "outline"} size="sm" borderWidth="1px" {...props} mr={1} mb={1}>
      {props.children}
    </Button>
  )
}
